import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dropzone", 
    "fileInput", 
    "preview", 
    "fileName", 
    "fileSize",
    "progress",
    "progressBar",
    "progressPercent",
    "textInput",
    "submitButton"
  ]
  
  connect() {
    this.setupDropzone()
  }
  
  setupDropzone() {
    // Prevent default drag behaviors
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.preventDefaults, false)
      document.body.addEventListener(eventName, this.preventDefaults, false)
    })
  }
  
  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }
  
  handleDragOver(e) {
    e.preventDefault()
    this.dropzoneTarget.classList.add('border-primary', 'bg-base-200')
  }
  
  handleDragLeave(e) {
    e.preventDefault()
    this.dropzoneTarget.classList.remove('border-primary', 'bg-base-200')
  }
  
  handleDrop(e) {
    e.preventDefault()
    this.dropzoneTarget.classList.remove('border-primary', 'bg-base-200')
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      this.handleFile(files[0])
    }
  }
  
  triggerFileSelect(e) {
    e.preventDefault()
    this.fileInputTarget.click()
  }
  
  handleFileSelect(e) {
    const files = e.target.files
    if (files.length > 0) {
      this.handleFile(files[0])
    }
  }
  
  handleFile(file) {
    // Validate file type
    const validTypes = [
      'text/plain',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/markdown',
      'application/rtf',
      'text/rtf'
    ]
    
    // Check file extension if MIME type is not recognized
    const validExtensions = ['.txt', '.pdf', '.docx', '.doc', '.md', '.rtf']
    const fileName = file.name.toLowerCase()
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext))
    
    if (!validTypes.includes(file.type) && !hasValidExtension) {
      alert('Please upload a valid transcript file (TXT, PDF, DOCX, MD, or RTF)')
      return
    }
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB in bytes
    if (file.size > maxSize) {
      alert('File size must be less than 10MB')
      return
    }
    
    // Update preview
    this.showFilePreview(file)
    
    // Store file reference
    this.selectedFile = file
    
    // Update file input
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    this.fileInputTarget.files = dataTransfer.files
  }
  
  showFilePreview(file) {
    this.fileNameTarget.textContent = file.name
    this.fileSizeTarget.textContent = this.formatFileSize(file.size)
    this.previewTarget.classList.remove('hidden')
  }
  
  removeFile(e) {
    e.preventDefault()
    
    // Clear file input
    this.fileInputTarget.value = ''
    
    // Hide preview
    this.previewTarget.classList.add('hidden')
    
    // Clear stored reference
    this.selectedFile = null
  }
  
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }
  
  // Handle form submission with progress
  handleSubmit(e) {
    // Check if either file or text is provided
    const hasFile = this.fileInputTarget.files.length > 0
    const hasText = this.textInputTarget.value.trim().length > 0
    
    if (!hasFile && !hasText) {
      e.preventDefault()
      alert('Please upload a file or paste transcript text')
      return false
    }
    
    // Show progress if file upload
    if (hasFile) {
      this.showProgress()
    }
    
    return true
  }
  
  showProgress() {
    this.progressTarget.classList.remove('hidden')
    this.submitButtonTarget.disabled = true
    
    // Simulate progress (in real app, use actual upload progress)
    let progress = 0
    const interval = setInterval(() => {
      progress += Math.random() * 15
      if (progress > 90) {
        progress = 90 // Cap at 90% until server responds
        clearInterval(interval)
      }
      
      this.updateProgress(Math.round(progress))
    }, 200)
  }
  
  updateProgress(percent) {
    this.progressBarTarget.value = percent
    this.progressPercentTarget.textContent = percent + '%'
  }
  
  // Handle successful upload
  uploadSuccess() {
    this.updateProgress(100)
    setTimeout(() => {
      this.progressTarget.classList.add('hidden')
      this.submitButtonTarget.disabled = false
    }, 500)
  }
  
  // Handle upload error
  uploadError(error) {
    this.progressTarget.classList.add('hidden')
    this.submitButtonTarget.disabled = false
    alert('Upload failed: ' + error)
  }
}